import { useEffect, useState } from "react";
import { Checkbox } from "@cofano/cck";
import { Box, Typography } from "@mui/material";
import { Language } from "imports/Utils";
import Attributes from "./Attributes";
import Tier from "./Tier";
import { AttributeType, convertPropertiesListToFormData, convertPropertiesToList } from "./utils";

export type Properties = Array<{
	[key in AttributeType]? : {
		[key: string]: number[];
	}
} & {
	tier: number;
}>;

export type PropertyItem = {
	tier: number;
	row: number;
	bay: number;
	attribute: AttributeType;
};

interface ShipBayplanProps {
	rows: number;
	bays: number;
	tiers: number;
	value: Properties | null;
	onChange?: (properties: Properties) => void;
}

const ShipBayplan = ({ rows, bays, tiers, value, onChange }: ShipBayplanProps) => {

	const [ properties, setProperties ] = useState<PropertyItem[]>(convertPropertiesToList(value));
	const [ selectedAttribute, selectAttribute ] = useState<AttributeType | undefined>(undefined);
	const [ showEvenBay, setShowEvenBay ] = useState(false);

	const onChangeAllowed = onChange ? true : false;

	const isMatch = (item: PropertyItem, tier: number, row: number, bay: number) => item.tier === tier && item.row === row && item.bay === bay;

	const handleOnChange = (attribute: AttributeType, tier: number, row: number, bay: number) => {

		const hasItem = properties.find(it => isMatch(it, tier, row, bay));

		if (hasItem) {

			const newProperties = [ ...properties.map(it => ({
				...it,
				attribute: isMatch(it, tier, row, bay) ? attribute : it.attribute,
			})) ] as PropertyItem[];

			if (onChange) {
				onChange(convertPropertiesListToFormData(newProperties));
			}
		} else {

			const newProperties = [ ...properties, {
				tier: tier,
				row: row,
				bay: bay,
				attribute: attribute,
			} ] as PropertyItem[];

			if (onChange) {
				onChange(convertPropertiesListToFormData(newProperties));
			}
		}
	};

	const handleOnDelete = (tier: number, row: number, bay: number) => {
		const newProperties = [ ...properties ].filter(it => !isMatch(it, tier, row, bay));

		if (onChange) {
			onChange(convertPropertiesListToFormData(newProperties));
		}
	};

	useEffect(() => {
		setProperties(convertPropertiesToList(value));
	}, [ value ]);

	return (
		<Box
			p={2}
			sx={{
				backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.background.paper : "#F0F0F0",
				overflowX: "auto",
			}}>
			<Box
				mb={2}
				sx={{
					alignItems: "center",
					display: "flex",
					justifyContent: "space-between",
				}}>
				<Typography
					variant="h4"
					fontWeight="medium"
					fontSize="18px"
					color="text.primary">
					{Language("BAYPLAN")}
				</Typography>

				<Box sx={{ width: "auto" }}>
					<Checkbox
						label={Language("BAYPLAN_SHOW_EVEN_BAY")}
						checked={showEvenBay}
						onChange={event => setShowEvenBay(event.target.checked)}
						size="small" />
				</Box>
			</Box>

			<Attributes
				selectedAttribute={selectedAttribute}
				onSelect={selectAttribute}
				onChangeAllowed={onChangeAllowed} />

			{Array.from(Array(tiers).keys()).map((index) => (
				<Tier
					index={index}
					tiers={tiers}
					rows={rows}
					bays={bays}
					properties={properties}
					onChange={handleOnChange}
					onDelete={handleOnDelete}
					selectedAttribute={selectedAttribute}
					showEvenBay={showEvenBay}
					onChangeAllowed={onChangeAllowed}
					key={index} />
			))}
		</Box>
	);
};

export default ShipBayplan;
